import { InformationCircleIcon } from 'kfone-component-library/svgIcons';
import { odsToastNotification } from 'kfone-component-library';
import { ToastNotificationVariant } from 'kfone-component-library/enums';
import type { ToastNotificationProps } from 'kfone-component-library/types';
import type { UpsertRtiTemplateBody } from 'model/interfaces/rti-template';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getRtiTemplate, RTI_TEMPLATE_VERSION_DEFAULT, upsertRtiTemplate } from 'services';
import { useCommonStore, useSettingsStore } from 'store';
import type { RtiCalculationMethodologyState } from 'model/interfaces/rti-template';
import {
  defaultCalculationMethodology,
  isCalculationMethodologySelectionValid,
  methodologyStateToPayload,
} from 'utilities/rti-calculation-methodology';
import { normalizeRtiAggregatedCompensation } from 'utilities/rti-aggregated-compensation';
import {
  normalizeRtiMinimumDataThreshold,
  type RtiMinimumDataThresholdModel,
} from 'utilities/rti-minimum-data-threshold';
import { normalizeRtiComparisonGroup } from 'utilities/rti-comparison-group';
import { normalizeRtiEmployeeDataFields } from 'utilities/rti-employee-data-fields';
import {
  comparisonGroupHasAtLeastOneSelected,
  employeeDataFieldsHasAtLeastOneSelected,
  fieldSectionHasAtLeastOneSelected,
} from 'utilities/rti-template-field-selection';
import { rtiTemplateDataToSettingsPatch } from 'utilities/rti-template-store-sync';
import { getPayequityToastPositionStyle } from 'utilities/rti-toast-messege';
import { RtiCalculationMethodology } from './rti-calculation-methodology';
import { RtiAggregatedCompensation } from './rti-aggregated-compensation';
import { RtiMinimumDataThreshold } from './rti-minimum-data-threshold';
import { RtiComparisonGroups } from './rti-comparison-groups';
import { RtiEmployeeDataFields } from './rti-employee-data-fields';
import { RtiReportFooter } from './rti-report-footer';
import { RtiReportHeader } from './rti-report-header';
import type { RtiComparisonGroupModel } from 'model/interfaces/rti-comparison-group';
import type {
  RtiEmployeeDataFields as RtiEmployeeDataFieldsModel,
  RtiFieldSection,
} from 'model/interfaces/rti-employee-data-fields';

type RtiTemplateRestPayload = Pick<
  UpsertRtiTemplateBody,
  'comparison_group' | 'company_wide_report' | 'minimum_data_threshold'
>;

const emptyTemplateRest = (): RtiTemplateRestPayload => ({
  comparison_group: {},
  company_wide_report: {},
  minimum_data_threshold: {},
});

function isUpsertSuccess(responseCode: unknown): boolean {
  return responseCode === 200 || responseCode === '200';
}

export type RtiReportConfigurationRef = {
  saveNow: () => Promise<boolean>;
};

export type RtiReportConfigurationProps = {
  onRtiEditAccessLoaded?: (isEditAccess: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
};

export const RtiReportConfiguration = forwardRef<RtiReportConfigurationRef, RtiReportConfigurationProps>(
  function RtiReportConfiguration({ onRtiEditAccessLoaded, onDirtyChange }, ref) {
    const onEditAccessCallbackRef = useRef(onRtiEditAccessLoaded);
    onEditAccessCallbackRef.current = onRtiEditAccessLoaded;
    const onDirtyChangeRef = useRef(onDirtyChange);
    onDirtyChangeRef.current = onDirtyChange;

    const { t } = useTranslation();
    const { pathname: routePathname } = useLocation();
    const updateRtiSettings = useSettingsStore((s) => s.updateRtiSettings);
    const [headerText, setHeaderText] = useState('');
    const [footerText, setFooterText] = useState('');
    const [templateVersion, setTemplateVersion] = useState(RTI_TEMPLATE_VERSION_DEFAULT);
    const [calculationMethodology, setCalculationMethodology] = useState<RtiCalculationMethodologyState>(() =>
      defaultCalculationMethodology(),
    );
    const [employeeDataFields, setEmployeeDataFields] = useState<RtiEmployeeDataFieldsModel>(() =>
      normalizeRtiEmployeeDataFields(null),
    );
    const [templateRest, setTemplateRest] = useState<RtiTemplateRestPayload>(emptyTemplateRest);
    const [templateLoaded, setTemplateLoaded] = useState(false);
    const [isEditAccess, setIsEditAccess] = useState(false);

    const aggregatedCompensationFields = useMemo((): RtiFieldSection => {
      const cwr = templateRest.company_wide_report as Record<string, unknown> | undefined;
      return normalizeRtiAggregatedCompensation(cwr?.compensation);
    }, [templateRest.company_wide_report]);

    const setAggregatedCompensationFields = useCallback((next: RtiFieldSection) => {
      setTemplateRest((prev) => {
        const prevCwr =
          prev.company_wide_report &&
          typeof prev.company_wide_report === 'object' &&
          !Array.isArray(prev.company_wide_report)
            ? { ...(prev.company_wide_report as Record<string, unknown>) }
            : {};
        prevCwr.compensation = next;
        return { ...prev, company_wide_report: prevCwr };
      });
    }, []);

    const minimumDataThresholdFields = useMemo(
      () => normalizeRtiMinimumDataThreshold(templateRest.minimum_data_threshold),
      [templateRest.minimum_data_threshold],
    );

    const setMinimumDataThresholdFields = useCallback((next: RtiMinimumDataThresholdModel) => {
      setTemplateRest((prev) => ({ ...prev, minimum_data_threshold: next }));
    }, []);

    const comparisonGroupModel = useMemo(
      () => normalizeRtiComparisonGroup(templateRest.comparison_group),
      [templateRest.comparison_group],
    );

    const setComparisonGroupFields = useCallback((next: RtiComparisonGroupModel) => {
      setTemplateRest((prev) => ({
        ...prev,
        comparison_group: next as unknown as Record<string, unknown>,
      }));
    }, []);

    const baselineReadyRef = useRef(false);
    const lastPersistedJsonRef = useRef<string>('');
    const persistQueueTailRef = useRef<Promise<boolean>>(Promise.resolve(true));

    const buildUpsertBody = useCallback((): UpsertRtiTemplateBody => {
      return {
        report_header: headerText,
        report_footer: footerText,
        comparison_group: normalizeRtiComparisonGroup(templateRest.comparison_group) as Record<string, unknown>,
        company_wide_report: templateRest.company_wide_report,
        employee_data_fields: employeeDataFields,
        minimum_data_threshold: normalizeRtiMinimumDataThreshold(templateRest.minimum_data_threshold),
        calculation_methodology: methodologyStateToPayload(calculationMethodology),
      };
    }, [headerText, footerText, templateRest, employeeDataFields, calculationMethodology]);

    const buildUpsertBodyRef = useRef(buildUpsertBody);
    buildUpsertBodyRef.current = buildUpsertBody;
    const templateVersionRef = useRef(templateVersion);
    templateVersionRef.current = templateVersion;
    const templateLoadedRef = useRef(templateLoaded);
    templateLoadedRef.current = templateLoaded;
    const isEditAccessRef = useRef(isEditAccess);
    isEditAccessRef.current = isEditAccess;

    const persistRtiTemplate = useCallback(async (): Promise<boolean> => {
      const run = async (): Promise<boolean> => {
        if (!templateLoadedRef.current || !baselineReadyRef.current) return false;
        if (!isEditAccessRef.current) return false;

        let body: UpsertRtiTemplateBody;
        let json: string;
        try {
          body = buildUpsertBodyRef.current();
          json = JSON.stringify(body);
        } catch (e) {
          console.error('RTI template: failed to build save payload', e);
          odsToastNotification({
            description: t('pe.settings.rti.saveError'),
            variant: ToastNotificationVariant.Error,
            options: {
              toastId: 'payequity-global-toast',
              closeButton: true,
              autoClose: 3000,
              style: getPayequityToastPositionStyle(routePathname),
            },
          });
          return false;
        }

        type SelectionToastItem = { description: string; toastId: string; autoClose: number };
        const selectionToasts: SelectionToastItem[] = [];

        if (!isCalculationMethodologySelectionValid(body.calculation_methodology)) {
          selectionToasts.push({
            description: t('pe.settings.rti.methodology.selectionRequired'),
            toastId: 'rti-toast-methodology',
            autoClose: 3000,
          });
        }

        const edf = normalizeRtiEmployeeDataFields(body.employee_data_fields);
        if (!employeeDataFieldsHasAtLeastOneSelected(edf)) {
          selectionToasts.push({
            description: t('pe.settings.rti.employeeData.selectionRequired'),
            toastId: 'rti-toast-employee-data',
            autoClose: 5000,
          });
        }

        const cwr = body.company_wide_report;
        const comp =
          cwr && typeof cwr === 'object' && !Array.isArray(cwr)
            ? (cwr as Record<string, unknown>).compensation
            : undefined;
        const agg = normalizeRtiAggregatedCompensation(comp);
        if (!fieldSectionHasAtLeastOneSelected(agg)) {
          selectionToasts.push({
            description: t('pe.settings.rti.aggregatedCompensation.selectionRequired'),
            toastId: 'rti-toast-aggregated-compensation',
            autoClose: 5000,
          });
        }

        const cg = normalizeRtiComparisonGroup(body.comparison_group);
        if (!comparisonGroupHasAtLeastOneSelected(cg)) {
          selectionToasts.push({
            description: t('pe.settings.rti.comparisonGroups.selectionRequired'),
            toastId: 'rti-toast-comparison-groups',
            autoClose: 5000,
          });
        }

        if (selectionToasts.length > 0) {
          for (const toast of selectionToasts) {
            odsToastNotification({
              description: toast.description,
              variant: ToastNotificationVariant.Error,
              options: {
                toastId: toast.toastId,
                closeButton: true,
                autoClose: toast.autoClose,
                style: getPayequityToastPositionStyle(routePathname),
              },
            });
          }
          return false;
        }

        if (json === lastPersistedJsonRef.current) {
          return true;
        }

        try {
          const res = await upsertRtiTemplate(templateVersionRef.current, body);
          if (isUpsertSuccess(res?.responseCode)) {
            lastPersistedJsonRef.current = json;
            onDirtyChangeRef.current?.(false);
            return true;
          }
          const notification: ToastNotificationProps = {
            description: t('pe.settings.rti.saveError'),
            variant: ToastNotificationVariant.Error,
            options: {
              toastId: 'payequity-global-toast',
              closeButton: true,
              autoClose: 3000,
              style: getPayequityToastPositionStyle(routePathname),
            },
          };
          odsToastNotification(notification);
          return false;
        } catch {
          const notification: ToastNotificationProps = {
            description: t('pe.settings.rti.saveError'),
            variant: ToastNotificationVariant.Error,
            options: {
              toastId: 'payequity-global-toast',
              closeButton: true,
              autoClose: 3000,
              style: getPayequityToastPositionStyle(routePathname),
            },
          };
          odsToastNotification(notification);
          return false;
        }
      };

      const queued = persistQueueTailRef.current.then(run);
      persistQueueTailRef.current = queued.catch(() => false);
      return queued;
    }, [routePathname, t]);

    useImperativeHandle(
      ref,
      () => ({
        saveNow: async () => {
          try {
            return (await persistRtiTemplate()) === true;
          } catch {
            return false;
          }
        },
      }),
      [persistRtiTemplate],
    );

    useEffect(() => {
      let cancelled = false;
      const { updateSpinnerShow } = useCommonStore.getState();
      updateSpinnerShow(false);
      baselineReadyRef.current = false;
      setTemplateLoaded(false);
      setIsEditAccess(false);

      void getRtiTemplate()
        .then((res) => {
          if (cancelled) return;
          if (res?.responseCode === 200 && res?.data) {
            const d = res.data;
            const patch = rtiTemplateDataToSettingsPatch(d);
            const canEdit = patch.rtiIsEditAccess === true;
            setIsEditAccess(canEdit);
            onEditAccessCallbackRef.current?.(canEdit);

            setHeaderText(patch.rtiReportHeader ?? '');
            setFooterText(patch.rtiReportFooter ?? '');
            setCalculationMethodology(patch.rtiCalculationMethodology ?? defaultCalculationMethodology());
            setTemplateVersion(patch.rtiTemplateVersion ?? RTI_TEMPLATE_VERSION_DEFAULT);
            setEmployeeDataFields(
              patch.rtiEmployeeDataFields ?? normalizeRtiEmployeeDataFields(null),
            );
            setTemplateRest({
              comparison_group: patch.rtiComparisonGroup ?? {},
              company_wide_report: patch.rtiCompanyWideReport ?? {},
              minimum_data_threshold: patch.rtiMinimumDataThreshold ?? {},
            });
            setTemplateLoaded(true);
          } else {
            setIsEditAccess(false);
            onEditAccessCallbackRef.current?.(false);
            useSettingsStore.getState().updateRtiSettings({
              rtiTemplateLoaded: false,
              rtiIsEditAccess: false,
            });
          }
        })
        .catch((err) => {
          console.error('Failed to load RTI template', err);
          if (!cancelled) {
            setIsEditAccess(false);
            onEditAccessCallbackRef.current?.(false);
            useSettingsStore.getState().updateRtiSettings({
              rtiTemplateLoaded: false,
              rtiIsEditAccess: false,
            });
          }
        })
        .finally(() => {
          if (!cancelled) {
            useCommonStore.getState().updateSpinnerShow(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }, []);

    const fieldsDisabled = !isEditAccess;

    useEffect(() => {
      if (!templateLoaded) return;

      let snapshot: string;
      try {
        snapshot = JSON.stringify(buildUpsertBody());
      } catch {
        return;
      }

      if (!baselineReadyRef.current) {
        baselineReadyRef.current = true;
        lastPersistedJsonRef.current = snapshot;
        return;
      }

      onDirtyChangeRef.current?.(snapshot !== lastPersistedJsonRef.current);
    }, [templateLoaded, buildUpsertBody]);

    useEffect(() => {
      if (!templateLoaded) return;
      updateRtiSettings({
        rtiReportHeader: headerText,
        rtiReportFooter: footerText,
        rtiTemplateVersion: templateVersion,
        rtiCalculationMethodology: calculationMethodology,
        rtiEmployeeDataFields: employeeDataFields,
        rtiComparisonGroup: normalizeRtiComparisonGroup(templateRest.comparison_group) as Record<string, unknown>,
        rtiAggregatedCompensation: aggregatedCompensationFields,
        rtiCompanyWideReport: templateRest.company_wide_report,
        rtiMinimumDataThreshold: normalizeRtiMinimumDataThreshold(templateRest.minimum_data_threshold),
        rtiTemplateLoaded: true,
        rtiIsEditAccess: isEditAccess,
      });
    }, [
      templateLoaded,
      headerText,
      footerText,
      templateVersion,
      calculationMethodology,
      employeeDataFields,
      aggregatedCompensationFields,
      templateRest,
      isEditAccess,
      updateRtiSettings,
    ]);

    return (
      <div className='flex flex-col gap-8'>
        <div className='flex flex-col gap-4'>
          <h2 className='text-ods-font-styles-heading-xsmall font-bold text-ods-color-brand-neutral-black'>
            {t('pe.settings.tabRti')}
          </h2>
          <p className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small text-[#1D1D1D]'>
            {t('pe.settings.rti.description')}
          </p>
          <div
            className='flex items-center gap-3 rounded border-l-[5px] border-l-[#005CC7] bg-[#EFF3FF] px-4 py-3'
            role='status'
          >
            <InformationCircleIcon className='h-5 w-5 shrink-0 text-[#005CC7]' aria-hidden />
            <div className='min-w-0 font-ods-font-family-secondary text-ods-font-styles-body-regular-small text-ods-color-brand-neutral-900'>
              <p className='mb-1 font-bold text-[#1E3A5F]'>{t('pe.settings.rti.reminderTitle')}</p>
              <p className='font-normal text-ods-color-brand-neutral-800'>
                <Trans i18nKey='pe.settings.rti.reminderBod' components={{ bold: <strong className='font-semibold' /> }} />
              </p>
            </div>
          </div>
          {templateLoaded && !isEditAccess ? (
            <div
              className='flex items-center gap-3 rounded border-l-[5px] border-l-[#6C6F75] bg-[#F5F5F5] px-4 py-3'
              role='status'
            >
              <InformationCircleIcon className='h-5 w-5 shrink-0 text-[#6C6F75]' aria-hidden />
              <p className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal text-[#1D1D1D]'>
                {t('pe.settings.rti.readOnlyBanner')}
              </p>
            </div>
          ) : null}
        </div>

        <div className='flex flex-col gap-10'>
          <RtiReportHeader value={headerText} onChange={setHeaderText} disabled={fieldsDisabled} />
          <RtiCalculationMethodology
            value={calculationMethodology}
            onChange={setCalculationMethodology}
            disabled={fieldsDisabled}
          />
          <RtiEmployeeDataFields value={employeeDataFields} onChange={setEmployeeDataFields} disabled={fieldsDisabled} />
          <RtiAggregatedCompensation
            value={aggregatedCompensationFields}
            onChange={setAggregatedCompensationFields}
            disabled={fieldsDisabled}
          />
          <RtiMinimumDataThreshold
            value={minimumDataThresholdFields}
            onChange={setMinimumDataThresholdFields}
            disabled={fieldsDisabled}
          />
          <RtiComparisonGroups
            value={comparisonGroupModel}
            onChange={setComparisonGroupFields}
            disabled={fieldsDisabled}
          />
          <RtiReportFooter value={footerText} onChange={setFooterText} disabled={fieldsDisabled} />
        </div>
      </div>
    );
  },
);
